"use strict";

const OVERLAY_SELECTOR = "[data-site-search]";
const PANEL_SELECTOR = "[data-site-search-panel]";
const CLOSE_SELECTOR = "[data-site-search-close]";
const FORM_SELECTOR = "[data-site-search-form]";
const INPUT_SELECTOR = "[data-site-search-input]";
const RESULTS_SELECTOR = "[data-site-search-results]";
const EMPTY_SELECTOR = "[data-site-search-empty]";
const TOGGLE_SELECTOR = "#searchToggle";

const AJAX_ACTION = "site_search_action";
const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

function debounce(fn, delay) {
    let timer = null;

    return function (...args) {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => fn.apply(this, args), delay);
    };
}

async function fetchSiteSearch(search) {
    const data = new FormData();
    data.append("action", AJAX_ACTION);
    data.append("nonce", window.WPURLS.nonce);
    data.append("search", search);

    if (window.WPURLS.lang) {
        data.append("lang", window.WPURLS.lang);
    }

    const response = await fetch(window.WPURLS.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: data,
    });

    const payload = await response.json();

    return payload && payload.success ? payload.data : null;
}

function setOpenState(overlay, toggle, isOpen) {
    if (isOpen) {
        overlay.removeAttribute("hidden");
        overlay.setAttribute("aria-hidden", "false");
        document.body.classList.add("no-scroll", "site-search-open");
    } else {
        overlay.setAttribute("hidden", "");
        overlay.setAttribute("aria-hidden", "true");
        document.body.classList.remove("no-scroll", "site-search-open");
    }

    if (toggle) {
        toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
        toggle.setAttribute(
            "aria-label",
            isOpen ? toggle.dataset.closeLabel : toggle.dataset.openLabel
        );
    }
}

function clearResults(results, emptyState) {
    if (results) {
        results.innerHTML = "";
        results.setAttribute("aria-busy", "false");
    }

    if (emptyState) {
        emptyState.setAttribute("hidden", "");
    }
}

function initSiteSearch() {
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    const toggle = document.querySelector(TOGGLE_SELECTOR);
    const panel = overlay ? overlay.querySelector(PANEL_SELECTOR) : null;
    const closeBtn = overlay ? overlay.querySelector(CLOSE_SELECTOR) : null;
    const form = overlay ? overlay.querySelector(FORM_SELECTOR) : null;
    const input = overlay ? overlay.querySelector(INPUT_SELECTOR) : null;
    const results = overlay ? overlay.querySelector(RESULTS_SELECTOR) : null;
    const emptyState = overlay ? overlay.querySelector(EMPTY_SELECTOR) : null;

    if (!overlay || !toggle || !panel || !input || !results || !window.WPURLS) {
        return;
    }

    toggle.dataset.openLabel =
        toggle.getAttribute("aria-label") || "Apri ricerca";
    toggle.dataset.closeLabel =
        toggle.dataset.closeLabel || "Chiudi ricerca";

    let activeRequest = 0;

    function open() {
        setOpenState(overlay, toggle, true);
        input.value = "";
        input.dispatchEvent(new Event("input", { bubbles: true }));
        clearResults(results, emptyState);

        window.requestAnimationFrame(() => {
            input.focus();
        });
    }

    function close() {
        if (overlay.hasAttribute("hidden")) {
            return;
        }

        setOpenState(overlay, toggle, false);
        clearResults(results, emptyState);
        input.value = "";
        input.dispatchEvent(new Event("input", { bubbles: true }));
        toggle.focus();
    }

    async function updateResults() {
        const query = input.value.trim();
        const requestId = ++activeRequest;

        if (query.length < MIN_CHARS) {
            clearResults(results, emptyState);
            return;
        }

        results.setAttribute("aria-busy", "true");

        try {
            const payload = await fetchSiteSearch(query);

            if (requestId !== activeRequest || payload === null) {
                return;
            }

            results.innerHTML = payload.html || "";

            if (emptyState) {
                if (payload.count > 0) {
                    emptyState.setAttribute("hidden", "");
                } else {
                    emptyState.removeAttribute("hidden");
                }
            }
        } finally {
            if (requestId === activeRequest) {
                results.setAttribute("aria-busy", "false");
            }
        }
    }

    const debouncedUpdate = debounce(updateResults, DEBOUNCE_MS);

    toggle.addEventListener("click", (event) => {
        event.preventDefault();

        if (overlay.hasAttribute("hidden")) {
            open();
        } else {
            close();
        }
    });

    if (closeBtn) {
        closeBtn.addEventListener("click", (event) => {
            event.preventDefault();
            close();
        });
    }

    if (form) {
        form.addEventListener("submit", (event) => {
            event.preventDefault();
            updateResults();
        });
    }

    input.addEventListener("input", debouncedUpdate);

    input.addEventListener("search", () => {
        updateResults();
    });

    overlay.addEventListener("click", (event) => {
        if (event.target === overlay) {
            close();
        }
    });

    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !overlay.hasAttribute("hidden")) {
            event.preventDefault();
            close();
        }
    });
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSiteSearch, { once: true });
} else {
    initSiteSearch();
}
